import type { ReactNode } from "react";
import { cx } from "./ui.js";

/**
 * A lupa que amplia uma miniatura **sem escolhê-la**: um botão à parte, irmão da miniatura e posto
 * sobre o canto dela. Nunca filho — `<button>` dentro de `<button>` é HTML inválido, e o toque na
 * lupa subiria até a miniatura e a escolheria.
 *
 * O desenho tem 28 px; o botão, 44: é a área que o dedo acerta. Quem a usa põe a posição
 * (`absolute right-0 top-0`) na `className`, dentro de um ancestral `relative`.
 */
export function LupaDeAmpliar({
  rotulo,
  aoClicar,
  className,
}: {
  /** O que a lupa amplia, com o nome: "Ampliar a foto de Maria". */
  rotulo: string;
  aoClicar: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-label={rotulo}
      title="Ampliar"
      onClick={aoClicar}
      className={cx("group/lupa grid size-11 place-items-center rounded-full", className)}
    >
      <span
        aria-hidden
        className="grid size-7 place-items-center rounded-full bg-fundo/85 text-texto shadow-sm ring-1 ring-black/40 transition-colors group-hover/lupa:bg-fundo"
      >
        <svg
          viewBox="0 0 24 24"
          className="size-4"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.25"
          strokeLinecap="round"
        >
          <circle cx="10.5" cy="10.5" r="6.5" />
          <path d="m15.5 15.5 5 5" />
        </svg>
      </span>
    </button>
  );
}

/**
 * Uma imagem grande que é, toda ela, o botão de ampliar. O "Ampliar" no canto é o que a denuncia:
 * uma imagem parada não diz que dá para tocar. Tudo dentro é `span` ou `img`, que é o que o HTML
 * aceita dentro de um botão.
 */
export function ImagemAmpliavel({
  rotulo,
  aoAmpliar,
  className,
  children,
}: {
  /** "Ampliar a foto de Maria": o que o leitor de tela anuncia no lugar da imagem. */
  rotulo: string;
  aoAmpliar: () => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={rotulo}
      onClick={aoAmpliar}
      className={cx("group relative block w-fit max-w-full cursor-zoom-in rounded-peca", className)}
    >
      {children}
      <span
        aria-hidden
        className="pointer-events-none absolute right-2 bottom-2 rounded-full bg-fundo/85 px-2.5 py-1 text-[0.75rem] font-medium text-texto transition-colors group-hover:bg-fundo"
      >
        Ampliar
      </span>
    </button>
  );
}
